import { useRouter } from 'next/router';
import { createContext, useReducer, useContext, ReactNode, useEffect } from 'react';
import { debounce } from 'lodash-es';
import { configureScope, captureException } from '@sentry/nextjs';
import {
  initialAppState,
  rootReducer,
  isStateVersionCompatible,
  doesStateHaveDefaultKeys,
  validateState,
} from '@/state';
import patchAppState from '@/state/patch';
import { initialState as flowInitialState } from '@/state/flow';
import logger from '@/lib/clientLogger';
import { ClientLogger } from '@care/utils';
import getVertical from '@/utilities/verticalUtils';
import { SessionStorageHelper } from '@/utilities/SessionStorageHelper';
import { AppAction, AppDispatch, AppState } from '@/types/app';
import { StateCheckingResponse } from '@/types/common';
import { LOCAL_STORAGE_STATE_KEY, CZEN_BASE_PATH, FLOWS, CLIENT_FEATURE_FLAGS } from '@/constants';
import { useFeatureFlags } from '@/components/FeatureFlagsContext';
import { AnalyticsHelper } from '@/utilities/analyticsHelper';
import logClickEvent from '@/utilities/log-click-event';
import { useClickEventLogger } from '@/components/hooks/useClickEventLogger';
import { GtmEvent, GtmService } from '@/utilities/GtmService';
import { getFeatureFlagValue } from '@/utilities/featureFlagHelper';

type AppStateProviderProps = {
  children: ReactNode;
  initialStateOverride?: AppState;
  czenJSessionId?: string | undefined;
  referrerCookie?: string | undefined | null;
  testCellCookieProvider?: string;
  userHasAccount?: boolean;
  memberUuid?: string;
  isImpersonated?: boolean;
};

function getStateFromStorageOrDefault(defaultState: AppState) {
  let stateFromStorageJson;
  const sessionStorageAvailable = SessionStorageHelper.isAvailable();

  if (sessionStorageAvailable) {
    stateFromStorageJson = SessionStorageHelper.getItem(LOCAL_STORAGE_STATE_KEY);
  } else {
    logger.warn({ event: 'sessionStorageUnavailable' });
  }

  if (stateFromStorageJson) {
    const stateFromStorage = JSON.parse(stateFromStorageJson) as AppState;
    try {
      const patchedState = patchAppState(stateFromStorage, defaultState);

      if (isStateVersionCompatible(patchedState) && doesStateHaveDefaultKeys(patchedState)) {
        return patchedState;
      }
      logger.warn({ event: 'invalidStateFromLocalStorage', state: stateFromStorageJson });
    } catch (e) {
      captureException(e, { level: 'warning' });
    }
  }
  return defaultState;
}

function persistState(state: AppState) {
  SessionStorageHelper.setItem(LOCAL_STORAGE_STATE_KEY, JSON.stringify(state));
}

const debouncePersistState = debounce(persistState, 500, {
  leading: true,
  trailing: true,
  maxWait: 1000,
});

function reducerWrapper(state: AppState, action: AppAction) {
  const nextState = rootReducer(state, action);
  debouncePersistState(nextState);
  return nextState;
}

export function redirectIfInvalidState(state: AppState, pathname: string): StateCheckingResponse {
  if (
    typeof Intl === 'undefined' ||
    typeof Intl.NumberFormat === 'undefined' ||
    typeof Intl.DateTimeFormat === 'undefined'
  ) {
    // TODO: Remove this with SC-214
    logger.error({ event: 'IntlUnavailable', source: pathname, invalidState: state });
    return {
      redirectCalled: true,
      readableMessage: 'Safari 9 and lower is not currently supported',
    };
  }

  const isStateValid = validateState(state, pathname);
  const redirect = !isStateValid;

  if (redirect) {
    SessionStorageHelper.removeItem(LOCAL_STORAGE_STATE_KEY);

    window.location.assign(CZEN_BASE_PATH);
  }

  return { redirectCalled: redirect };
}

export function getFlowName(
  pathname: string,
  oldFlowName?: string,
  isReducedDaycareFunnel?: boolean
): string {
  let newFlowName = Object.values(FLOWS).find((flow) => pathname.includes(flow.path))?.name;

  if (isReducedDaycareFunnel && newFlowName === FLOWS.SEEKER_DAYCARE_CHILD_CARE.name) {
    newFlowName = FLOWS.SEEKER_DAYCARE_CHILD_CARE_REDUCED.name;
  }

  // Don't allow shifting from multi vertical flow to default cc seeker flow name
  if (
    newFlowName === FLOWS.SEEKER_CHILD_CARE.name &&
    oldFlowName === FLOWS.SEEKER_CHILD_CARE_WITH_ADD_ON_VERTICALS.name
  ) {
    return FLOWS.SEEKER_CHILD_CARE_WITH_ADD_ON_VERTICALS.name;
  }
  // Don't allow shifting from dc reduced to seeker cc flow names
  if (
    [FLOWS.SEEKER_CHILD_CARE.name].includes(newFlowName as string) &&
    oldFlowName === FLOWS.SEEKER_DAYCARE_CHILD_CARE_REDUCED.name
  ) {
    return FLOWS.SEEKER_DAYCARE_CHILD_CARE_REDUCED.name;
  }
  // Don't allow shifting from dc kind to seeker cc or dc reduced flow names
  if (
    [FLOWS.SEEKER_CHILD_CARE.name, FLOWS.SEEKER_DAYCARE_CHILD_CARE_REDUCED.name].includes(
      newFlowName as string
    ) &&
    oldFlowName === FLOWS.SEEKER_DAYCARE_CARE_KIND.name
  ) {
    return FLOWS.SEEKER_DAYCARE_CARE_KIND.name;
  }

  // Don't allow shifting from in-facility to seeker flow name
  if (newFlowName === FLOWS.SEEKER.name && oldFlowName === FLOWS.SEEKER_IN_FACILITY.name) {
    return FLOWS.SEEKER_IN_FACILITY.name;
  }

  // Don't allow shifting from skip help type to seeker flow name
  if (
    newFlowName === FLOWS.SEEKER.name &&
    oldFlowName === FLOWS.SEEKER_SENIOR_CARE_SKIP_HELP_TYPE_NOT_SURE.name
  ) {
    return FLOWS.SEEKER_SENIOR_CARE_SKIP_HELP_TYPE_NOT_SURE.name;
  }

  // Don't allow shifting from skip help type in home to seeker flow name
  if (
    newFlowName === FLOWS.SEEKER.name &&
    oldFlowName === FLOWS.SEEKER_SENIOR_CARE_SKIP_HELP_TYPE_IN_HOME.name
  ) {
    return FLOWS.SEEKER_SENIOR_CARE_SKIP_HELP_TYPE_IN_HOME.name;
  }

  // Don't allow shifting from multi vertical to seeker flow name
  if (
    newFlowName === FLOWS.SEEKER.name &&
    oldFlowName === FLOWS.SEEKER_WITH_ADD_ON_VERTICALS.name
  ) {
    return FLOWS.SEEKER_WITH_ADD_ON_VERTICALS.name;
  }

  // Don't allow shifting from multi vertical to seeker_v2 flow name
  if (
    newFlowName === FLOWS.SEEKER_SENIOR_CARE_V2.name &&
    oldFlowName === FLOWS.SEEKER_WITH_ADD_ON_VERTICALS.name
  ) {
    return FLOWS.SEEKER_WITH_ADD_ON_VERTICALS.name;
  }

  // Don't allow shifting from seeker_v2 to seeker flow name
  if (newFlowName === FLOWS.SEEKER.name && oldFlowName === FLOWS.SEEKER_SENIOR_CARE_V2.name) {
    return FLOWS.SEEKER_SENIOR_CARE_V2.name;
  }

  // Don't allow shifting from redesign flow to non-redesign flow
  if (
    newFlowName === FLOWS.SEEKER_HOUSEKEEPING.name &&
    oldFlowName === FLOWS.SEEKER_HOUSEKEEPING_REDESIGN.name
  ) {
    return FLOWS.SEEKER_HOUSEKEEPING_REDESIGN.name;
  }

  // Don't allow shifting from VHP specific flow to non-VHP flow
  if (
    newFlowName === FLOWS.SEEKER_HOUSEKEEPING.name &&
    oldFlowName === FLOWS.SEEKER_HOUSEKEEPING_FROM_VHP.name
  ) {
    return FLOWS.SEEKER_HOUSEKEEPING_FROM_VHP.name;
  }
  if (
    newFlowName === FLOWS.SEEKER_PET_CARE.name &&
    oldFlowName === FLOWS.SEEKER_PET_CARE_FROM_VHP.name
  ) {
    return FLOWS.SEEKER_PET_CARE_FROM_VHP.name;
  }

  if (
    newFlowName === FLOWS.SEEKER_PET_CARE.name &&
    oldFlowName === FLOWS.SEEKER_PET_CARE_WITH_ADD_ON_VERTICALS.name
  ) {
    return FLOWS.SEEKER_PET_CARE_WITH_ADD_ON_VERTICALS.name;
  }

  if (
    newFlowName === FLOWS.SEEKER_PET_CARE.name &&
    oldFlowName === FLOWS.SEEKER_PET_CARE_FROM_VHP_WITH_ADD_ON_VERTICAL.name
  ) {
    return FLOWS.SEEKER_PET_CARE_FROM_VHP_WITH_ADD_ON_VERTICAL.name;
  }

  if (
    newFlowName === FLOWS.SEEKER_TUTORING.name &&
    oldFlowName === FLOWS.SEEKER_TUTORING_FROM_VHP.name
  ) {
    return FLOWS.SEEKER_TUTORING_FROM_VHP.name;
  }

  if (
    newFlowName === FLOWS.SEEKER_TUTORING.name &&
    oldFlowName === FLOWS.SEEKER_TUTORING_WITH_ADD_ON_VERTICALS.name
  ) {
    return FLOWS.SEEKER_TUTORING_WITH_ADD_ON_VERTICALS.name;
  }

  if (
    newFlowName === FLOWS.SEEKER_TUTORING.name &&
    oldFlowName === FLOWS.SEEKER_TUTORING_FROM_VHP_WITH_ADD_ON_VERTICALS.name
  ) {
    return FLOWS.SEEKER_TUTORING_FROM_VHP_WITH_ADD_ON_VERTICALS.name;
  }

  // During enrollment flows we create a user, auth it and redirect back.
  // To prevent flow updates on the authCallback page we need the next condition.
  if (pathname.startsWith('/authCallback')) {
    return oldFlowName || '';
  }

  return newFlowName ?? '';
}

function useFlowDetection(
  dispatch: AppDispatch,
  oldFlowName: string,
  isReducedDaycareFunnel?: boolean
) {
  const { pathname } = useRouter();
  const newFlowName = getFlowName(pathname, oldFlowName, isReducedDaycareFunnel);
  const vertical = getVertical();
  let memberType = pathname?.includes('seeker') ? 'seeker' : '';
  if (pathname?.includes('provider')) {
    memberType = 'provider';
  }
  ClientLogger.setLoggerStatics({ flow: newFlowName, vertical });
  useEffect(() => {
    dispatch({ type: 'setFlowName', flowName: newFlowName });

    configureScope((scope) => {
      scope.setTags({
        flow: newFlowName,
        member_type: memberType,
        vertical,
      });
    });
  }, [newFlowName]);
}

const AppStateContext = createContext<AppState>(initialAppState);
const AppDispatchContext = createContext<AppDispatch>(() =>
  // eslint-disable-next-line no-console
  console.warn(
    'Calling default dispatch is a no-op! You are probably consuming app dispatch in a component above AppStateProvider'
  )
);

export const AppStateProvider = ({
  children,
  initialStateOverride,
  czenJSessionId,
  referrerCookie,
  testCellCookieProvider,
  userHasAccount,
  memberUuid,
  isImpersonated,
}: AppStateProviderProps) => {
  const hydratedInitialAppState = {
    ...initialAppState,
    flow: {
      ...flowInitialState,
      czenJSessionId,
      referrerCookie,
      userHasAccount,
      memberUuid,
      isImpersonated,
    },
    providerCC: {
      ...initialAppState.providerCC,
      testCellCookieProvider,
    },
  };
  const featureFlags = useFeatureFlags();
  const sessionStorageRetrievalSuccessEvaluation =
    featureFlags?.flags[CLIENT_FEATURE_FLAGS.SESSION_STORAGE_RETRIEVAL_SUCCESS];
  const shouldMeasureSessionStorageRetrievalSuccess =
    sessionStorageRetrievalSuccessEvaluation?.variationIndex === 1;
  const trackClickGQLCallEvaluation =
    featureFlags?.flags[CLIENT_FEATURE_FLAGS.TRACK_CLICK_GQL_CALL];

  const safetySiftOldConsoleEnabled = getFeatureFlagValue(
    CLIENT_FEATURE_FLAGS.SAFETY_SIFT_OLD_CONSOLE_ENABLED
  );
  const safetySiftUserJourneyEnabled = getFeatureFlagValue(
    CLIENT_FEATURE_FLAGS.SAFETY_SIFT_USER_JOURNEY_ENABLED
  );

  // https://carecom.atlassian.net/browse/PN-5779: daycare-enrollment-reduced-v2 A/B test
  // index: 0 - reduced flow off - value = 0
  // index: 1 - reduced flow on - value = 1
  const reducedDaycareFlag = featureFlags?.flags[CLIENT_FEATURE_FLAGS.DAYCARE_REDUCED_FUNNEL_V2];

  const router = useRouter();

  const logSessionStorageError = () => {
    logger.info({ event: 'sessionStorageStateUnavailable', path: router.pathname });
    AnalyticsHelper.logEvent({
      name: 'sessionStorageStateUnavailable',
    });
  };
  const launchDarklyWrappedGetStateFromStorageOrDefault = (defaultState: AppState) => {
    if (shouldMeasureSessionStorageRetrievalSuccess) {
      try {
        const sessionStorageState = SessionStorageHelper.getItem(LOCAL_STORAGE_STATE_KEY);
        if (sessionStorageState && sessionStorageState.length > 0) {
          const stateFromStorage = JSON.parse(sessionStorageState);
          if (stateFromStorage) {
            logger.info({ event: 'sessionStorageStateAvailable', path: router.pathname });
          } else {
            logSessionStorageError();
          }
        } else {
          logSessionStorageError();
        }
      } catch (_e) {
        logSessionStorageError();
      }
    }
    // memberUUID will not be present in the persisted storage until after the user gets logged in in certain flows
    // we also want to preserve the initialStateOverride here
    const hydratedDefaultState = {
      ...defaultState,
      flow: {
        ...defaultState.flow,
        memberUuid: memberUuid || initialStateOverride?.flow.memberUuid,
      },
    };

    return getStateFromStorageOrDefault(hydratedDefaultState);
  };

  const [state, dispatch] = useReducer(
    reducerWrapper,
    initialStateOverride ?? hydratedInitialAppState,
    launchDarklyWrappedGetStateFromStorageOrDefault
  );

  const fireClickEventGQL = useClickEventLogger();
  useEffect(() => {
    (async () => {
      if (!state.flow.clickEventLogged && memberUuid) {
        dispatch({ type: 'setClickEventLogged', clickEventLogged: true });
        await logClickEvent(memberUuid, true, trackClickGQLCallEvaluation, fireClickEventGQL);
      }
    })();
  }, [state.flow.clickEventLogged, memberUuid]);

  const inReducedDaycareFunnel = reducedDaycareFlag?.value === 1;
  useFlowDetection(dispatch, state.flow.flowName, inReducedDaycareFunnel);
  const routePath = router.asPath?.split('?')[0];
  const stateCheckResult = redirectIfInvalidState(state, routePath);
  if (stateCheckResult.redirectCalled) {
    if (stateCheckResult.readableMessage) {
      return (
        <>
          <p>Redirecting to Member Home Page...</p>
          <p>{stateCheckResult.readableMessage}</p>
        </>
      );
    }
    return <></>;
  }

  useEffect(() => {
    // push to old console
    if (
      safetySiftOldConsoleEnabled &&
      !isImpersonated &&
      (state.flow.externalId || state.flow.czenJSessionId)
    ) {
      const data = {
        sessionId: state.flow.czenJSessionId,
        externalId: state.flow.externalId ?? undefined,
      };
      GtmService.pushDefinedEvent(GtmEvent.SiftTrackPageViewLegacy, data, {
        ignoreDuplicatesControl: true,
      });
    }

    // push to new console
    if (safetySiftUserJourneyEnabled && !isImpersonated && state.flow.memberUuid) {
      const data = {
        memberUuid: state.flow.memberUuid,
      };
      GtmService.pushDefinedEvent(GtmEvent.SiftTrackPageView, data, {
        ignoreDuplicatesControl: true,
      });
    }
  }, [routePath]);

  return (
    <AppDispatchContext.Provider value={dispatch}>
      <AppStateContext.Provider value={state}>{children}</AppStateContext.Provider>
    </AppDispatchContext.Provider>
  );
};

AppStateProvider.defaultProps = {
  initialStateOverride: undefined,
  czenJSessionId: undefined,
  referrerCookie: undefined,
  testCellCookieProvider: undefined,
  userHasAccount: false,
  memberUuid: undefined,
  isImpersonated: undefined,
};

export const useAppState = () => useContext(AppStateContext);
export const useAppDispatch = () => useContext(AppDispatchContext);

export const useSeekerState = () => useContext(AppStateContext).seeker;
export const useProviderState = () => useContext(AppStateContext).provider;
export const useProviderCCState = () => useContext(AppStateContext).providerCC;
export const useProviderMVState = () => useContext(AppStateContext).providerMV;
export const useFlowState = () => useContext(AppStateContext).flow;
export const useSeekerCCState = () => useContext(AppStateContext).seekerCC;
export const useSeekerHKState = () => useContext(AppStateContext).seekerHK;
export const useSeekerPCState = () => useContext(AppStateContext).seekerPC;
export const useSeekerTUState = () => useContext(AppStateContext).seekerTU;
export const useLtcgState = () => useContext(AppStateContext).ltcg;
export const useFeatureFlagSharedDataState = () =>
  useContext(AppStateContext).featureFlagSharedData;
